import React, { useEffect, useId, useMemo, useState } from "react";
import { Search, ArrowUpward, ArrowDownward, UnfoldMore } from "@mui/icons-material";
import Button from "./Button";

export const SearchBar = ({ value, onChange, placeholder = "Search..." }) => {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-md bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
      />
    </div>
  );
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const getSortValue = (row, column) => {
  if (typeof column.sortValue === "function") return column.sortValue(row);
  return row?.[column.key];
};

const compareValues = (left, right, direction) => {
  const emptyLeft = left === null || left === undefined || left === "";
  const emptyRight = right === null || right === undefined || right === "";

  if (emptyLeft && emptyRight) return 0;
  if (emptyLeft) return 1;
  if (emptyRight) return -1;

  const bothNumeric =
    typeof left === "number" ||
    typeof right === "number" ||
    (typeof left === "string" && typeof right === "string" && left.trim() !== "" && right.trim() !== "" && !Number.isNaN(Number(left)) && !Number.isNaN(Number(right)));

  if (bothNumeric && !Number.isNaN(Number(left)) && !Number.isNaN(Number(right))) {
    const diff = Number(left) - Number(right);
    return direction === "asc" ? diff : -diff;
  }

  const leftText = String(left).toLowerCase();
  const rightText = String(right).toLowerCase();
  if (leftText < rightText) return direction === "asc" ? -1 : 1;
  if (leftText > rightText) return direction === "asc" ? 1 : -1;
  return 0;
};

const DataTable = ({
  columns = [],
  rows = [],
  loading = false,
  search,
  onSearchChange,
  searchPlaceholder = "Search...",
  onEdit,
  onDelete,
  extraActions,
  emptyMessage = "No records found.",
  pageSize: initialPageSize = 10,
  defaultSortKey,
  defaultSortDir = "asc",
  rowKey = "id",
  actionsLabel = "Actions"
}) => {
  const sortSelectId = useId();
  const [sortKey, setSortKey] = useState(defaultSortKey || columns[0]?.key || "");
  const [sortDir, setSortDir] = useState(defaultSortDir);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  useEffect(() => {
    setPage(1);
  }, [search, sortKey, sortDir, pageSize, rows.length]);

  const handleSort = (column) => {
    if (column.sortable === false) return;
    if (sortKey === column.key) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(column.key);
    setSortDir("asc");
  };

  const sortedRows = useMemo(() => {
    const column = columns.find((item) => item.key === sortKey);
    if (!column) return rows;

    return [...rows].sort((a, b) =>
      compareValues(getSortValue(a, column), getSortValue(b, column), sortDir)
    );
  }, [rows, columns, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = sortedRows.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageRows = sortedRows.slice(startIndex, startIndex + pageSize);
  const endIndex = startIndex + pageRows.length;
  const showActions = Boolean(onEdit || onDelete || extraActions);
  const colSpan = columns.length + (showActions ? 1 : 0);
  const sortableColumns = columns.filter((column) => column.sortable !== false);
  const [primaryColumn, ...detailColumns] = columns;

  const renderCell = (row, column) => (column.render ? column.render(row) : (row[column.key] ?? "-"));

  const renderActions = (row) => (
    <>
      {extraActions ? extraActions(row) : null}
      {onEdit ? (
        <Button size="sm" variant="success" icon="update" onClick={() => onEdit(row)}>
          Update
        </Button>
      ) : null}
      {onDelete ? (
        <Button size="sm" variant="danger" icon="delete" onClick={() => onDelete(row)}>
          Delete
        </Button>
      ) : null}
    </>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {typeof onSearchChange === "function" ? (
        <div className="p-3 sm:p-4 border-b border-gray-100">
          <div className="md:max-w-md">
            <SearchBar
              value={search}
              onChange={onSearchChange}
              placeholder={searchPlaceholder}
            />
          </div>
        </div>
      ) : null}

      {/* Phones: stacked cards */}
      <div className="md:hidden">
        {sortableColumns.length > 0 ? (
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 bg-gray-50 text-sm text-gray-600">
            <label htmlFor={sortSelectId} className="shrink-0">Sort by</label>
            <select
              id={sortSelectId}
              value={sortKey}
              onChange={(event) => setSortKey(event.target.value)}
              className="min-w-0 flex-1 px-2 py-1.5 border border-gray-300 rounded-md bg-white outline-none focus:ring-2 focus:ring-teal-500"
            >
              {sortableColumns.map((column) => (
                <option key={column.key} value={column.key}>
                  {typeof column.label === "string" ? column.label : column.key}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setSortDir((current) => (current === "asc" ? "desc" : "asc"))}
              className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-md border border-gray-300 bg-white text-teal-700"
              aria-label={sortDir === "asc" ? "Sort descending" : "Sort ascending"}
            >
              {sortDir === "asc" ? <ArrowUpward sx={{ fontSize: 18 }} /> : <ArrowDownward sx={{ fontSize: 18 }} />}
            </button>
          </div>
        ) : null}

        {loading ? (
          <div className="flex justify-center px-4 py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          </div>
        ) : pageRows.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-gray-500">{emptyMessage}</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {pageRows.map((row) => (
              <li key={row[rowKey] ?? JSON.stringify(row)} className="px-4 py-4">
                {primaryColumn ? (
                  <div className="text-[15px] font-semibold text-gray-900 break-words">
                    {renderCell(row, primaryColumn)}
                  </div>
                ) : null}
                {detailColumns.length > 0 ? (
                  <dl className="mt-2 grid grid-cols-1 min-[420px]:grid-cols-2 gap-x-4 gap-y-2">
                    {detailColumns.map((column) => (
                      <div key={column.key} className="min-w-0">
                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{column.label}</dt>
                        <dd className="text-sm text-gray-900 break-words">{renderCell(row, column)}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
                {showActions ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2">{renderActions(row)}</div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Tablets and up: table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {columns.map((column) => {
                const isActive = sortKey === column.key;
                const sortable = column.sortable !== false;

                return (
                  <th
                    key={column.key}
                    className={`px-4 lg:px-6 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap ${sortable ? "cursor-pointer select-none hover:text-teal-700" : ""}`}
                    onClick={() => handleSort(column)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {column.label}
                      {sortable ? (
                        isActive ? (
                          sortDir === "asc" ? (
                            <ArrowUpward sx={{ fontSize: 16 }} className="text-teal-600" />
                          ) : (
                            <ArrowDownward sx={{ fontSize: 16 }} className="text-teal-600" />
                          )
                        ) : (
                          <UnfoldMore sx={{ fontSize: 16 }} className="text-gray-400" />
                        )
                      ) : null}
                    </span>
                  </th>
                );
              })}
              {showActions ? (
                <th className="px-4 lg:px-6 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">
                  {actionsLabel}
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="px-6 py-12 text-center text-gray-500">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                  </div>
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-6 py-12 text-center text-gray-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr key={row[rowKey] ?? JSON.stringify(row)} className="hover:bg-gray-50 transition-colors">
                  {columns.map((column) => (
                    <td key={column.key} className={`px-4 lg:px-6 py-4 text-sm text-gray-900 ${column.className || ""}`}>
                      {renderCell(row, column)}
                    </td>
                  ))}
                  {showActions ? (
                    <td className="px-4 lg:px-6 py-4">
                      <div className="flex flex-wrap items-center gap-2">{renderActions(row)}</div>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-3 sm:px-4 py-3 border-t border-gray-100 bg-gray-50">
        <p className="text-sm text-gray-600">
          {sortedRows.length === 0
            ? "Showing 0 records"
            : `Showing ${startIndex + 1}-${endIndex} of ${sortedRows.length}`}
        </p>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="text-sm text-gray-600 inline-flex items-center gap-2">
            Rows
            <select
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
              className="px-2 py-1.5 border border-gray-300 rounded-md bg-white text-sm outline-none focus:ring-2 focus:ring-teal-500"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={currentPage <= 1}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700 min-w-[90px] text-center">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={currentPage >= totalPages}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
